import type { HierarchyNode } from '../../types/exploration';
import { applyBubbleCaps, hierarchyCapKey } from './bubbleCap';

/** The target volume plus the paging state a re-capped branch has to honour. */
type PinContext = {
  ebsId: string;
  overflowPages: Record<string, number> | undefined;
};

function isTargetVolume(node: HierarchyNode, ebsId: string): boolean {
  return node.type === 'volume' && node.volume?.ebsId === ebsId;
}

/**
 * Does this subtree hold the volume anywhere — including inside the hidden set of
 * a `+ N More` bubble, which `applyBubbleCaps` parks on `_overflowChildren`
 * rather than `children`?
 */
function subtreeHasVolume(node: HierarchyNode, ebsId: string): boolean {
  if (isTargetVolume(node, ebsId)) return true;
  // `_volumes` is the flat roll-up of everything under a group node, so this both
  // short-circuits the recursion and is the only signal for shallow views, where
  // `applyShallowDepth` strips volume leaves and leaves `children: null` behind.
  if (node._volumes?.some((v) => v.ebsId === ebsId)) return true;
  if ((node.children ?? []).some((child) => subtreeHasVolume(child, ebsId))) return true;
  return (node._overflowChildren ?? []).some((child) => subtreeHasVolume(child, ebsId));
}

/**
 * Is the volume actually drawn, i.e. reachable without passing through an overflow
 * bubble?
 *
 * Deliberately does NOT consult `_volumes`: that roll-up is present on every group
 * node, and the wrapper directly under the root carries the entire scope, so
 * honouring it here would report every in-scope volume as already drawn and
 * disable hoisting altogether.
 */
function isVolumeRendered(node: HierarchyNode, ebsId: string): boolean {
  if (node._isOverflow) return false;
  if (isTargetVolume(node, ebsId)) return true;
  return (node.children ?? []).some((child) => isVolumeRendered(child, ebsId));
}

/** Deep copy of the node objects on a branch; volumes themselves stay shared. */
function cloneBranch(node: HierarchyNode): HierarchyNode {
  const copy: HierarchyNode = { ...node };
  if (node.children) copy.children = node.children.map(cloneBranch);
  return copy;
}

/**
 * Apply the standard page caps to a branch lifted out of `_overflowChildren` —
 * those subtrees were never capped, so without this a single hoist could dump
 * thousands of bubbles onto the canvas.
 *
 * `overflowPages` has to be threaded in: the main capping pass never recurses into
 * `_overflowChildren`, so this is the only place a `+ N More` inside a hoisted
 * branch is ever built, and capping it at page 0 unconditionally would leave that
 * bubble unexpandable.
 */
function capHoistedBranch(node: HierarchyNode, pathPrefix: string, ctx: PinContext): HierarchyNode {
  const branch = cloneBranch(node);
  applyBubbleCaps(branch, pathPrefix, ctx.overflowPages);
  return branch;
}

/**
 * Replace `overflow` in `children` with the hoisted branch plus a smaller
 * `+ N More` bubble for whatever is still hidden.
 */
function hoistOutOfOverflow(
  children: HierarchyNode[],
  overflow: HierarchyNode,
  hoisted: HierarchyNode,
  pathPrefix: string,
  ctx: PinContext
): HierarchyNode[] {
  const rest = (overflow._overflowChildren ?? []).filter((c) => c !== hoisted);
  const branchKey = hierarchyCapKey(pathPrefix, hoisted);
  const branch = pinVolume(capHoistedBranch(hoisted, branchKey, ctx), branchKey, ctx);
  const next: HierarchyNode[] = [];

  for (const child of children) {
    if (child !== overflow) {
      next.push(child);
      continue;
    }
    next.push(branch);
    if (rest.length > 0) {
      next.push({
        ...overflow,
        name: `+ ${rest.length} More`,
        _hidden: rest.length,
        _overflowChildren: rest,
        value: Math.max((overflow.value ?? 0) - (hoisted.value ?? 0), 1),
      });
    }
  }

  return next;
}

/** The overflow bubble hiding the volume, together with the branch to lift out of it. */
function findHiddenBranch(
  children: HierarchyNode[],
  ebsId: string
): { overflow: HierarchyNode; hoisted: HierarchyNode } | null {
  for (const child of children) {
    if (!child._isOverflow) continue;
    const hoisted = (child._overflowChildren ?? []).find((h) => subtreeHasVolume(h, ebsId));
    if (hoisted) return { overflow: child, hoisted };
  }
  return null;
}

/**
 * Return `node` with the target volume un-hidden, cloning only the nodes on the
 * path to it. Returns the same reference when nothing had to change.
 */
function pinVolume(node: HierarchyNode, pathPrefix: string, ctx: PinContext): HierarchyNode {
  const children = node.children;
  if (!children?.length) return node;

  const hidden = findHiddenBranch(children, ctx.ebsId);
  if (hidden) {
    return {
      ...node,
      children: hoistOutOfOverflow(children, hidden.overflow, hidden.hoisted, pathPrefix, ctx),
    };
  }

  for (let i = 0; i < children.length; i++) {
    const child = children[i]!;
    if (child._isOverflow || !subtreeHasVolume(child, ctx.ebsId)) continue;
    const pinned = pinVolume(child, hierarchyCapKey(pathPrefix, child), ctx);
    // Nothing to lift in this branch — keep looking, the volume may also sit under
    // a later sibling that does have it hidden behind a `+ N More`.
    if (pinned === child) continue;
    const next = children.slice();
    next[i] = pinned;
    return { ...node, children: next };
  }

  return node;
}

/**
 * Guarantee the selected volume has a bubble on the canvas.
 *
 * Selecting a volume re-roots the canvas at that volume's own location, which
 * resets `overflowPages` — so a volume the user just picked out of a `+ N More`
 * bubble would otherwise drop straight back into one, leaving the selection with
 * nothing to ring. Returns `root` unchanged (same reference) whenever the volume
 * is already drawn or is not in this scope, so the render memo downstream is only
 * invalidated when the tree really had to change.
 */
export function ensureVolumeVisible(
  root: HierarchyNode,
  ebsId: string | null | undefined,
  overflowPages?: Record<string, number>
): HierarchyNode {
  if (!ebsId) return root;
  if (isVolumeRendered(root, ebsId)) return root;
  if (!subtreeHasVolume(root, ebsId)) return root;
  return pinVolume(root, 'root', { ebsId, overflowPages });
}
